import { useEffect, useMemo, useState } from "react";
import { Dropdown, Form } from "react-bootstrap";
import { SearchSvg } from "../../svgFiles/SearchSvg";
import { AddSvg } from "../../svgFiles/AddSvg";
import Button from "../../components/shared/buttons/button";
import { useDebounce, showToast } from "../../helpers/Utils";
import {
  useGetRedemptionCodesQuery,
  useUpdateRedemptionCodeStatusMutation,
} from "../../app/redemptionApi/redemptionApi";
import RedemptionCodeTable from "./RedemptionCodeTable";
import AddRedemptionCodeModal from "./AddRedemptionCodeModal";
import RedemptionCodeSuccessModal from "./RedemptionCodeSuccessModal";
import "./redemptionCodes.css";

const PAGE_SIZE = 20;

const STATUS_FILTERS = [
  { value: "all", label: "All Statuses" },
  { value: "unused", label: "Unused" },
  { value: "redeemed", label: "Redeemed" },
  { value: "expired", label: "Expired" },
];

export default function RedemptionCodesPage() {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 400);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [createdCode, setCreatedCode] = useState("");

  const queryArgs = useMemo(
    () => ({
      page: showSelectedOnly ? 1 : page,
      limit: PAGE_SIZE,
      search: debouncedSearch,
      status: statusFilter,
      ids: showSelectedOnly ? selectedIds : undefined,
    }),
    [page, debouncedSearch, statusFilter, showSelectedOnly, selectedIds]
  );

  const { data, isLoading, isFetching, isError, refetch } =
    useGetRedemptionCodesQuery(queryArgs);
  const [updateStatus, { isLoading: isUpdating }] =
    useUpdateRedemptionCodeStatusMutation();

  const payload = data?.data || {};
  const items = payload.codes || [];
  const pagination = payload.pagination || {};
  const total = pagination.total || 0;
  const totalPages = pagination.totalPages || 1;

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
    setShowSelectedOnly(false);
  }, [debouncedSearch, statusFilter]);

  const handleStatusChange = async (status) => {
    if (selectedIds.length === 0) {
      showToast("Select at least one code", "error");
      return;
    }
    try {
      const result = await updateStatus({
        ids: selectedIds,
        status,
      }).unwrap();
      const updated = result?.data?.updated ?? selectedIds.length;
      showToast(
        updated === 1
          ? "Status updated"
          : `${updated} codes updated`,
        "success"
      );
      setSelectedIds([]);
      setShowSelectedOnly(false);
    } catch (err) {
      showToast(err?.data?.message || "Failed to update status", "error");
    }
  };

  return (
    <div className="redemption-page">
      <div className="redemption-card">
        <div className="redemption-toolbar">
          <div className="search redemption-search d-flex align-items-center gap-2">
            <SearchSvg />
            <input
              type="search"
              className="w-100"
              placeholder="Search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <div className="d-flex align-items-center gap-2">
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                className="redemption-filter-toggle"
              >
                {STATUS_FILTERS.find((f) => f.value === statusFilter)?.label || "All Statuses"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {STATUS_FILTERS.map((f) => (
                  <Dropdown.Item
                    key={f.value}
                    active={statusFilter === f.value}
                    onClick={() => setStatusFilter(f.value)}
                  >
                    {f.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="redemption-bulk-bar">
            <div className="d-flex align-items-center gap-3">
              <Form.Check
                type="checkbox"
                checked
                readOnly
                aria-label="Selected codes"
              />
              <strong>{selectedIds.length} Selected</strong>
              <Dropdown>
                <Dropdown.Toggle
                  variant="light"
                  className="redemption-status-toggle"
                  disabled={isUpdating}
                >
                  Change Status
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleStatusChange("unused")}>
                    Unused
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleStatusChange("expired")}>
                    Expired
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <Form.Check
              type="switch"
              id="show-all-selected"
              label="Show all selected"
              checked={showSelectedOnly}
              onChange={(e) => {
                setShowSelectedOnly(e.target.checked);
                setPage(1);
              }}
            />
          </div>
        )}

        {isError ? (
          <div className="text-center py-5">
            <p className="text-danger mb-3">Failed to load redemption codes</p>
            <Button label="Retry" onClick={refetch} />
          </div>
        ) : (
          <RedemptionCodeTable
            items={items}
            isLoading={isLoading || isFetching}
            page={pagination.page || page}
            pageSize={pagination.limit || PAGE_SIZE}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />
        )}
      </div>

      <AddRedemptionCodeModal
        show={showAddModal}
        setShow={setShowAddModal}
        onSuccess={(code) => {
          refetch();
          if (code) setCreatedCode(code);
        }}
      />
      <RedemptionCodeSuccessModal
        show={Boolean(createdCode)}
        setShow={(next) => {
          if (!next) setCreatedCode("");
        }}
        code={createdCode}
      />
    </div>
  );
}
